import { BuildClass } from '../../../../universal';
import { _, hljs, match, React } from '../../../lib';
import { MarkdownRenderer } from './markdown-renderer';
import { renderToolOutput } from './tool-renderers';
const svgProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
};
const BrainIcon = (p) => (React.createElement("img", { className: BuildClass({
        [p.className ?? '']: true,
        invert: p.isDark ?? false,
    }), src: "/static/img/i8/ios16o-brain-ai.svg" }));
const ToolIconSearch = (p) => (React.createElement("img", { className: BuildClass({
        [p.className ?? '']: true,
        invert: p.isDark ?? false,
    }), src: "/static/img/i8/ios16o-search.svg" }));
const ToolIconQuestion = (p) => (React.createElement("svg", { className: p.className, ...svgProps },
    React.createElement("circle", { cx: "12", cy: "12", r: "10" }),
    React.createElement("path", { d: "M9.1 9a3 3 0 1 1 4.8 2.4c-1.1 0.8-1.9 1.5-1.9 2.6" }),
    React.createElement("line", { x1: "12", y1: "17", x2: "12.01", y2: "17" })));
const ToolIconInfo = (p) => (React.createElement("svg", { className: p.className, ...svgProps },
    React.createElement("circle", { cx: "12", cy: "12", r: "10" }),
    React.createElement("line", { x1: "12", y1: "16", x2: "12", y2: "12" }),
    React.createElement("line", { x1: "12", y1: "8", x2: "12.01", y2: "8" })));
const ToolIconChatBubble = (p) => (React.createElement("img", { className: BuildClass({
        [p.className ?? '']: true,
        invert: p.isDark ?? false,
    }), src: "/static/img/i8/ios16o-chat-bubble.svg" }));
const ToolIconPresentation = (p) => (React.createElement("img", { className: BuildClass({
        [p.className ?? '']: true,
        invert: p.isDark ?? false,
    }), src: "/static/img/i8/ios16o-presentation.svg" }));
const ToolIconChange = (p) => (React.createElement("img", { className: BuildClass({
        [p.className ?? '']: true,
        invert: p.isDark ?? false,
    }), src: "/static/img/i8/ios16o-edit-property.svg" }));
const ChevronIcon = (p) => (React.createElement("svg", { className: p.className, ...svgProps },
    React.createElement("polyline", { points: "6 9 12 15 18 9" })));
const TEXT_MUTED = {
    color: 'oklch(var(--chat-l-text-muted) 0 0)',
};
const TEXT_NORMAL = {
    color: 'oklch(var(--chat-l-text-normal) 0 0)',
};
const TEXT_CONTENT = {
    color: 'oklch(var(--chat-l-text-content) 0 0)',
};
const itemBtnClass = 'w-full flex items-center gap-2 text-left py-0.5 px-1.5 -mx-1.5 rounded hover:bg-[oklch(var(--chat-l-text)_0_0_/_0.05)] transition-colors cursor-pointer';
const codeBlockClass = [
    'p-2 max-w-full overflow-x-auto rounded',
    'text-[10px] leading-[1.4]',
    'bg-[oklch(var(--chat-l-code)_0_0_/_0.5)]',
    'select-text',
];
const tokenCountChipClass = 'text-[10px] leading-none flex-shrink-0 rounded px-1 py-0.5 bg-[oklch(var(--chat-l-text)_0_0_/_0.06)]';
const titleChipBaseClass = [
    'inline-flex items-center max-w-[14rem] truncate',
    'rounded px-1.5 py-0.5',
    'text-[11px] leading-none font-medium',
];
const titleChipToneClass = {
    default: [
        'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue)_/_0.12)]',
        'text-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_var(--chat-hue))]',
    ],
    green: [
        'bg-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_145_/_0.12)]',
        'text-[oklch(var(--chat-l-accent)_var(--chat-c-accent)_145)]',
    ],
    grey: [
        'bg-[oklch(var(--chat-l-text)_0_0_/_0.06)]',
        'text-[oklch(var(--chat-l-text-muted)_0_0)]',
    ],
};
const getTitleChipClass = (tone) => [
    ...titleChipBaseClass,
    ...titleChipToneClass[tone],
];
export const AssistantTimeline = (props) => {
    const { timeline, collapseLevel, onCollapseLevelChange, isStreaming } = props;
    const canExpand = props.thoughtAccess !== 'none';
    const canExpandItems = props.thoughtAccess === 'full';
    const effectiveCollapseLevel = canExpand ? collapseLevel : 0;
    const isDark = props.theme === 'dark';
    const handleHeaderClick = () => {
        if (!canExpand) {
            return;
        }
        onCollapseLevelChange(collapseLevel === 0 ? 1 : 0);
    };
    const thinkingTimeText = formatThinkingTime(timeline.totalThinkingSeconds, isStreaming);
    const activitySummaryLabel = getTimelineActivitySummaryLabel(timeline, thinkingTimeText, canExpand);
    return (React.createElement("div", { className: BuildClass({
            'w-full rounded-lg p-2': true,
            'bg-[oklch(var(--chat-l-surface)_0_0_/_1)]': true,
        }) },
        React.createElement("div", { className: "w-full space-y-1" },
            timeline.leadingActivity && (canExpand || !isStreaming) && (React.createElement(ActivitySegmentView, { segment: timeline.leadingActivity, collapseLevel: effectiveCollapseLevel, expandedItems: props.expandedItems, onToggleItem: props.onToggleItem, onActivityClick: handleHeaderClick, canExpandItems: canExpandItems, canExpand: canExpand, summaryLabel: activitySummaryLabel, toolRenderers: props.toolRenderers, toolHandlers: props.toolHandlers, isDark: isDark, isStreaming: isStreaming })),
            timeline.textSegments.map((seg, idx) => (React.createElement(React.Fragment, { key: idx },
                React.createElement(FinalTextBlock, { text: seg.text.text, isStreaming: isStreaming && idx === timeline.textSegments.length - 1 }),
                seg.followingActivity && (React.createElement(ActivitySegmentView, { segment: seg.followingActivity, collapseLevel: effectiveCollapseLevel, expandedItems: props.expandedItems, onToggleItem: props.onToggleItem, onActivityClick: handleHeaderClick, canExpandItems: canExpandItems, canExpand: canExpand, summaryLabel: activitySummaryLabel, toolRenderers: props.toolRenderers, toolHandlers: props.toolHandlers, isDark: isDark, isStreaming: isStreaming }))))),
            isStreaming && (React.createElement(StreamingFooter, { currentStatus: canExpand ? props.currentStatus : null, startedAt: timeline.firstContentTimestamp ?? undefined, statusPrefix: canExpand ? 'Working... ' : '', isDark: isDark })))));
};
const StreamingFooter = (props) => {
    const [elapsed, setElapsed] = React.useState(0);
    React.useEffect(() => {
        if (!props.startedAt) {
            return;
        }
        const startTime = new Date(props.startedAt).getTime();
        const updateElapsed = () => {
            setElapsed(Math.floor((Date.now() - startTime) / 1000));
        };
        updateElapsed();
        const interval = setInterval(updateElapsed, 1000);
        return () => {
            clearInterval(interval);
        };
    }, [props.startedAt]);
    return (React.createElement("div", { className: "flex items-center gap-2 text-sm py-1 px-2", style: TEXT_MUTED },
        React.createElement("span", { className: "flex-shrink-0 animate-spin" },
            React.createElement("svg", { className: "w-4 h-4", viewBox: "0 0 24 24", fill: "none" },
                React.createElement("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "3" }),
                React.createElement("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" }))),
        React.createElement("span", { className: "truncate" }, props.currentStatus ??
            `${props.statusPrefix ?? 'Working... '}${elapsed}s`)));
};
export const TextBlockContent = (props) => (React.createElement("div", { className: "text-left select-text text-[oklch(var(--chat-l-text)_0_0)]" }, props.text.trim().length === 0 ? (React.createElement("div", { className: "text-[oklch(var(--chat-l-text-muted)_0_0)]" }, "Completed without text response")) : (React.createElement(MarkdownRenderer, { content: props.text, largeText: props.largeText && !props.isStreaming }))));
export const FinalTextBlock = (props) => (React.createElement("div", { className: BuildClass([
        'w-full px-2 py-2',
    ]) },
    React.createElement(TextBlockContent, { ...props, largeText: true })));
const ActivitySegmentView = (props) => {
    const { segment, collapseLevel } = props;
    if (collapseLevel === 0) {
        if (!props.summaryLabel) {
            return React.createElement(React.Fragment, null);
        }
        return (React.createElement(ActivitySummaryButton, { label: props.summaryLabel, isExpanded: false, canExpand: props.canExpand, onClick: props.onActivityClick, isDark: props.isDark }));
    }
    return (React.createElement("div", { className: "space-y-0.5" },
        React.createElement(ActivitySummaryButton, { label: props.summaryLabel, isExpanded: collapseLevel >= 1, canExpand: props.canExpand, onClick: props.onActivityClick, isDark: props.isDark }),
        segment.items.map((item, idx) => {
            const itemID = getItemID(item, idx);
            const isStepChild = item.type !== 'step' &&
                _.some(segment.items.slice(0, idx), { type: 'step' });
            const stepNumber = item.type === 'step'
                ? _.filter(segment.items.slice(0, idx + 1), { type: 'step' })
                    .length
                : undefined;
            return (React.createElement("div", { key: itemID, className: BuildClass({
                    'pl-2': isStepChild,
                }) },
                React.createElement(TimelineItemView, { item: item, itemID: itemID, isExpanded: props.expandedItems.has(itemID), onToggle: () => {
                        props.onToggleItem(itemID);
                    }, canExpandItems: props.canExpandItems, toolRenderers: props.toolRenderers, toolHandlers: props.toolHandlers, isDark: props.isDark, stepNumber: stepNumber })));
        }),
        !props.isStreaming && (React.createElement("div", { className: "h-px bg-[oklch(var(--chat-l-text)_0_0_/_0.12)]" }))));
};
const ActivitySummaryButton = (props) => (React.createElement("button", { type: "button", onClick: props.canExpand ? props.onClick : undefined, disabled: !props.canExpand, className: BuildClass({
        'flex items-center gap-2 py-1 px-2 text-xs rounded-full': true,
        'bg-[oklch(var(--chat-l-text)_0_0_/_0.05)]': true,
        'hover:bg-[oklch(var(--chat-l-text)_0_0_/_0.1)]': props.canExpand,
        'transition-colors cursor-pointer': props.canExpand,
        'cursor-default': !props.canExpand,
    }), style: TEXT_MUTED },
    React.createElement(BrainIcon, { className: "w-3 h-3", isDark: props.isDark }),
    React.createElement("span", null, props.label),
    props.canExpand && (React.createElement(ChevronIcon, { className: BuildClass({
            'w-3.5 h-3.5 opacity-60 transition-transform duration-200': true,
            'rotate-180': props.isExpanded,
        }) }))));
const TimelineItemView = (props) => {
    const { item, isExpanded, canExpandItems } = props;
    return match(item)
        .with({ type: 'step' }, () => React.createElement(StepDivider, { stepNumber: props.stepNumber }))
        .with({ type: 'reasoning' }, i => (React.createElement(ReasoningItemView, { block: i.block, isExpanded: isExpanded, canExpand: canExpandItems, onToggle: props.onToggle, isDark: props.isDark })))
        .with({ type: 'progress-note' }, i => (React.createElement(ProgressNoteItemView, { block: i.block, isExpanded: isExpanded, canExpand: canExpandItems, onToggle: props.onToggle, isDark: props.isDark })))
        .with({ type: 'response-message' }, i => (React.createElement(ResponseMessageItemView, { block: i.block, isDark: props.isDark })))
        .with({ type: 'error' }, i => React.createElement(ErrorItemView, { block: i.block }))
        .with({ type: 'search' }, { type: 'presentation' }, { type: 'change' }, i => (React.createElement(ToolItemView, { item: i, isExpanded: isExpanded, canExpand: canExpandItems, onToggle: props.onToggle, toolRenderers: props.toolRenderers, toolHandlers: props.toolHandlers, isDark: props.isDark })))
        .exhaustive();
};
export const StepDivider = (props) => (React.createElement("div", { className: "flex items-center gap-2 px-1.5" },
    React.createElement("span", { className: "text-xs uppercase tracking-wide text-[oklch(var(--chat-l-text-muted)_0_0)]" }, props.stepNumber == null ? 'Step' : `Step ${props.stepNumber}`)));
export const ProgressNoteItemView = (props) => {
    const wordCount = props.block.text.trim().split(/\s+/).filter(Boolean).length;
    const wordCountLabel = formatCountLabel(wordCount, 'word', 'words');
    return (React.createElement("div", null,
        React.createElement("button", { type: "button", onClick: props.canExpand ? props.onToggle : undefined, disabled: !props.canExpand, className: BuildClass({
                [itemBtnClass]: true,
                'cursor-pointer': props.canExpand,
                'cursor-default': !props.canExpand,
            }), style: TEXT_NORMAL },
            React.createElement(ToolIconChatBubble, { className: "w-4 h-4 flex-shrink-0", isDark: props.isDark }),
            React.createElement("span", { className: "min-w-0 truncate text-sm" }, "Progress note"),
            wordCountLabel && (React.createElement("span", { className: "text-xs flex-shrink-0", style: TEXT_MUTED }, wordCountLabel)),
            props.canExpand && (React.createElement(ChevronIcon, { className: BuildClass({
                    'w-3.5 h-3.5 opacity-60': true,
                    'transition-transform duration-200': true,
                    'rotate-180': props.isExpanded,
                }) }))),
        props.isExpanded && props.canExpand && (React.createElement("div", { className: "flex mt-1" },
            React.createElement("div", { className: "w-4 flex justify-center flex-shrink-0" },
                React.createElement("div", { className: "w-px h-full bg-[oklch(var(--chat-l-text)_0_0_/_0.15)]" })),
            React.createElement("div", { className: BuildClass({
                    'flex-1 min-w-0 py-1 px-2': true,
                    'text-sm leading-[1.5] select-text': true,
                    'bg-[oklch(var(--chat-l-surface)_0_0_/_0.3)]': true,
                }), style: TEXT_CONTENT },
                React.createElement(MarkdownRenderer, { content: props.block.text }))))));
};
export const ResponseMessageItemView = (props) => {
    const wordCount = props.block.text.trim().split(/\s+/).filter(Boolean).length;
    const wordCountLabel = formatCountLabel(wordCount, 'word', 'words');
    return (React.createElement("div", null,
        React.createElement("div", { className: BuildClass({
                [itemBtnClass]: true,
                'cursor-default': true,
            }), style: TEXT_NORMAL },
            React.createElement(ToolIconChatBubble, { className: "w-4 h-4 flex-shrink-0", isDark: props.isDark }),
            React.createElement("span", { className: "min-w-0 truncate text-sm" }, "Response message"),
            wordCountLabel && (React.createElement("span", { className: "text-xs flex-shrink-0", style: TEXT_MUTED }, wordCountLabel)))));
};
export const ErrorItemView = (props) => (React.createElement("div", { className: BuildClass([itemBtnClass, 'cursor-default text-red-600']) },
    React.createElement(ToolIconInfo, { className: "w-4 h-4 flex-shrink-0" }),
    React.createElement("span", { className: "min-w-0 text-sm" }, props.block.error)));
export const ReasoningItemView = (props) => {
    const { block, isExpanded, canExpand } = props;
    const wordCount = block.reasoning.summary.split(/\s+/).filter(Boolean).length;
    const title = block.reasoning.title || `Thinking (${wordCount} words)`;
    return (React.createElement("div", null,
        React.createElement("button", { type: "button", onClick: canExpand ? props.onToggle : undefined, disabled: !canExpand, className: BuildClass({
                [itemBtnClass]: true,
                'cursor-pointer': canExpand,
                'cursor-default': !canExpand,
            }), style: TEXT_NORMAL },
            React.createElement(BrainIcon, { className: "w-4 h-4 flex-shrink-0", isDark: props.isDark }),
            React.createElement("span", { className: "min-w-0 truncate text-sm" }, title),
            canExpand && (React.createElement(ChevronIcon, { className: BuildClass({
                    'w-3.5 h-3.5 opacity-60': true,
                    'transition-transform duration-200': true,
                    'rotate-180': isExpanded,
                }) }))),
        isExpanded && canExpand && (React.createElement(ReasoningDetailView, { summary: block.reasoning.summary }))));
};
export const ReasoningDetailView = (props) => (React.createElement("div", null,
    React.createElement("div", { className: "flex mt-1" },
        React.createElement("div", { className: "w-4 flex justify-center flex-shrink-0" },
            React.createElement("div", { className: "w-px h-full bg-[oklch(var(--chat-l-text)_0_0_/_0.15)]" })),
        React.createElement("div", { className: BuildClass({
                'flex-1 min-w-0 py-1 px-2': true,
                'text-sm leading-[1.5] select-text': true,
                'bg-[oklch(var(--chat-l-surface)_0_0_/_0.3)]': true,
                'text-[oklch(var(--chat-l-text-normal)_0_0)]': true,
            }) }, props.summary))));
export const ToolItemView = (props) => {
    const { item, isExpanded, canExpand } = props;
    const tool = item.block.tool;
    const hasTitle = Boolean(tool.title);
    const error = 'error' in tool ? tool.error : null;
    const hasError = Boolean(error);
    const outputTokenCount = React.useMemo(() => getToolOutputTokenCount(item), [item]);
    const outputTokenCountText = formatTokenCount(outputTokenCount);
    const IconComponent = item.type === 'search'
        ? tool.name === 'ask_follow_up_questions'
            ? ToolIconQuestion
            : tool.name === 'skills_read'
                ? ToolIconInfo
                : tool.name === 'lookup_tool_info'
                    ? ToolIconInfo
                    : ToolIconSearch
        : match(item.type)
            .with('presentation', () => ToolIconPresentation)
            .with('change', () => ToolIconChange)
            .exhaustive();
    return (React.createElement("div", null,
        React.createElement("button", { type: "button", onClick: canExpand ? props.onToggle : undefined, disabled: !canExpand, className: BuildClass({
                [itemBtnClass]: true,
                'cursor-pointer': canExpand,
                'cursor-default': !canExpand,
            }), style: TEXT_NORMAL },
            React.createElement(IconComponent, { className: "w-4 h-4 flex-shrink-0", isDark: props.isDark }),
            React.createElement(ToolTitleView, { title: tool.title ?? tool.name, hasError: hasError }),
            !hasTitle && (React.createElement("span", { className: "truncate flex-1 min-w-0 font-mono text-xs", style: TEXT_MUTED }, JSON.stringify(tool.input))),
            outputTokenCountText && outputTokenCount != null && (React.createElement("span", { className: tokenCountChipClass, style: TEXT_MUTED, title: `Approximately ${outputTokenCount.toLocaleString()} output tokens` }, outputTokenCountText)),
            canExpand && (React.createElement(ChevronIcon, { className: BuildClass({
                    'w-3.5 h-3.5 opacity-60 transition-transform duration-200': true,
                    'rotate-180': isExpanded,
                }) }))),
        isExpanded && canExpand && (React.createElement(ToolDetailView, { item: item, toolRenderers: props.toolRenderers, toolHandlers: props.toolHandlers }))));
};
const ToolTitleView = (props) => {
    const displayParts = React.useMemo(() => getDisplayToolTitleParts(parseToolTitleParts(props.title)), [props.title]);
    const chipTone = getToolTitleChipTone(props.title);
    return (React.createElement("span", { className: BuildClass({
            'min-w-0 flex flex-wrap items-center gap-x-1 gap-y-0.5': true,
            'font-medium text-sm': true,
            'text-red-600': props.hasError,
        }) }, displayParts.map((part, index) => part.type === 'chip' ? (React.createElement("span", { key: index, className: BuildClass(getTitleChipClass(chipTone)) }, part.text)) : (React.createElement("span", { key: index }, part.text)))));
};
const parseToolTitleParts = (title) => _.chain(title.split(/(`[^`]*`)/g))
    .map((part) => {
    if (part.length === 0) {
        return null;
    }
    if (part.startsWith('`') && part.endsWith('`')) {
        const text = part.slice(1, -1).trim();
        return text.length === 0 ? null : { type: 'chip', text };
    }
    const text = part.replace(/\s+/g, ' ').trim();
    return text.length === 0 ? null : { type: 'text', text };
})
    .compact()
    .value();
const getDisplayToolTitleParts = (parts) => {
    const chips = _.filter(parts, isToolTitleChip);
    const visibleChipCount = chips.length <= 4 && _.sumBy(chips, chip => chip.text.length) <= 56
        ? chips.length
        : 2;
    if (visibleChipCount >= chips.length) {
        return parts;
    }
    return _.chain(parts)
        .map((part, index) => {
        if (part.type === 'text') {
            return part;
        }
        const chipNumber = _.filter(parts.slice(0, index + 1), isToolTitleChip).length;
        if (chipNumber <= visibleChipCount) {
            return part;
        }
        if (chipNumber === visibleChipCount + 1) {
            return { type: 'chip', text: `+${chips.length - visibleChipCount}` };
        }
        return null;
    })
        .compact()
        .value();
};
const isToolTitleChip = (part) => part.type === 'chip';
const getToolTitleChipTone = (title) => title.startsWith('Skills ')
    ? 'green'
    : title.startsWith('Lookup tool definitions ')
        ? 'grey'
        : 'default';
export const ToolDetailView = (props) => {
    const { item } = props;
    const tool = item.block.tool;
    const hasTitle = Boolean(tool.title);
    const error = 'error' in tool ? tool.error : null;
    const readList = 'output' in tool ? getToolReadList(tool.name, tool.input, tool.output) : null;
    const inputDisplay = getToolInputDisplay(tool.name, tool.input);
    return (React.createElement("div", { className: "flex mt-1" },
        React.createElement("div", { className: "w-4 flex justify-center flex-shrink-0" },
            React.createElement("div", { className: "w-px h-full bg-[oklch(var(--chat-l-text)_0_0_/_0.15)]" })),
        React.createElement("div", { className: BuildClass({
                'flex-1 min-w-0 space-y-2 py-1 px-2': true,
                'bg-[oklch(var(--chat-l-surface)_0_0_/_0.3)]': true,
            }) },
            readList ? (React.createElement(ToolReadListSection, { heading: readList.heading, items: readList.items })) : (React.createElement(ToolInputSection, { toolName: inputDisplay.toolName, input: inputDisplay.input, showToolName: hasTitle || inputDisplay.toolName !== tool.name })),
            error && React.createElement(ToolErrorSection, { error: error }),
            !readList && (React.createElement(ToolOutputSection, { item: item, codeBlockClass: codeBlockClass, toolRenderers: props.toolRenderers, toolHandlers: props.toolHandlers })))));
};
export const ToolInputSection = (props) => (React.createElement("div", null,
    React.createElement("div", { className: "text-xs mb-1", style: TEXT_MUTED },
        "Input",
        props.showToolName && (React.createElement("span", { className: "font-mono ml-2" }, props.toolName))),
    React.createElement(HighlightedJsonBlock, { value: props.input })));
const ToolReadListSection = (props) => (React.createElement("div", null,
    React.createElement("div", { className: "text-xs mb-1", style: TEXT_MUTED }, props.heading),
    React.createElement("ul", { className: "list-disc pl-5 space-y-1 text-xs", style: TEXT_CONTENT }, props.items.map((item, index) => {
        const tokenText = formatTokenCount(item.tokenCount);
        return (React.createElement("li", { key: `${item.label}-${index}` },
            React.createElement("span", null, item.label),
            tokenText && (React.createElement("span", { className: BuildClass([tokenCountChipClass, 'ml-2']), style: TEXT_MUTED }, tokenText))));
    }))));
const HighlightedJsonBlock = (props) => {
    const json = React.useMemo(() => JSON.stringify(props.value, null, 2) ?? 'null', [props.value]);
    const highlighted = React.useMemo(() => hljs.highlight(json, { language: 'json' }).value, [json]);
    return (React.createElement("pre", { className: BuildClass(codeBlockClass), style: TEXT_CONTENT },
        React.createElement("code", { className: "hljs language-json", dangerouslySetInnerHTML: { __html: highlighted } })));
};
export const ToolErrorSection = (props) => (React.createElement("div", null,
    React.createElement("div", { className: "text-xs mb-1 text-red-500" }, "Error"),
    React.createElement("pre", { className: `${BuildClass(codeBlockClass)} !bg-red-50 !border-red-200 text-red-700` }, props.error)));
const ToolOutputSection = (props) => {
    const { item } = props;
    const output = 'output' in item.block.tool ? item.block.tool.output : null;
    if (isFileReadToolOutputHidden(item.block.tool.name, output)) {
        return null;
    }
    const toolCall = match(item)
        .with({ type: 'search' }, i => ({
        kind: 'search',
        tool: i.block.tool,
    }))
        .with({ type: 'presentation' }, i => ({
        kind: 'presentation',
        tool: i.block.tool,
    }))
        .with({ type: 'change' }, i => ({
        kind: 'change',
        tool: i.block.tool,
    }))
        .exhaustive();
    const customOutput = renderToolOutput(toolCall, {
        renderers: props.toolRenderers,
        ...props.toolHandlers,
    });
    if (customOutput) {
        return (React.createElement("div", null,
            React.createElement("div", { className: "text-xs mb-1", style: TEXT_MUTED }, "Output"),
            customOutput));
    }
    if (output == null) {
        return null;
    }
    return (React.createElement("div", null,
        React.createElement("div", { className: "text-xs mb-1", style: TEXT_MUTED }, "Output"),
        React.createElement("pre", { className: BuildClass(props.codeBlockClass), style: TEXT_CONTENT }, typeof output === 'string' ? output : JSON.stringify(output, null, 2))));
};
const formatThinkingTime = (seconds, isStreaming) => {
    if (isStreaming) {
        return 'Thinking...';
    }
    if (seconds < 1) {
        return 'Thought for <1s';
    }
    return `Thought for ${Math.round(seconds)}s`;
};
const getToolOutputTokenCount = (item) => {
    const output = 'output' in item.block.tool ? item.block.tool.output : null;
    if (isFileReadToolOutputHidden(item.block.tool.name, output)) {
        return null;
    }
    if (output == null) {
        return null;
    }
    const text = typeof output === 'string' ? output : JSON.stringify(output, null, 2);
    return estimateTokenCount(text);
};
const formatTokenCount = (tokens) => {
    if (tokens == null) {
        return null;
    }
    const roundedHundreds = Math.round(tokens / 100);
    if (roundedHundreds === 0) {
        return null;
    }
    return `${(roundedHundreds / 10).toFixed(1)}k ¤`;
};
const isFileReadToolOutputHidden = (toolName, output) => (toolName === 'read_file' || toolName === 'read_resident_note_attachment') &&
    _.isPlainObject(output) &&
    _.isString(_.get(output, 'type')) &&
    _.get(output, 'type') !== 'error';
const estimateTokenCount = (text) => Math.ceil(Math.max(text.length / 4, text.split(/\s+/).filter(Boolean).length));
const getToolReadList = (toolName, input, output) => {
    if (toolName === 'skills_read') {
        const items = getSkillReadListItems(input, output);
        if (!isCompleteToolReadList(items)) {
            return null;
        }
        return {
            heading: 'Skills',
            items,
        };
    }
    if (toolName === 'lookup_tool_info') {
        const items = getLookupToolDefinitionListItems(input, output);
        if (!isCompleteToolReadList(items)) {
            return null;
        }
        return {
            heading: 'Tool definitions',
            items,
        };
    }
    return null;
};
const getSkillReadListItems = (input, output) => {
    const skillBlocksBySlug = parseSkillOutputBlocks(output);
    return _.chain(getInputStringArray(input, 'skillSlugs'))
        .map(slug => {
        const block = skillBlocksBySlug[slug];
        return {
            label: block?.title ?? slug,
            tokenCount: block ? estimateTokenCount(block.content) : null,
        };
    })
        .value();
};
const getLookupToolDefinitionListItems = (input, output) => {
    const definitions = parseLookupToolDefinitionOutput(output);
    const definitionsByName = _.keyBy(definitions, definition => definition.toolName);
    const inputToolNames = getInputStringArray(input, 'toolNames');
    const toolNames = inputToolNames.length > 0
        ? inputToolNames
        : definitions.map(definition => definition.toolName);
    return _.chain(toolNames)
        .map(toolName => {
        const definition = definitionsByName[toolName];
        return {
            label: toolName,
            tokenCount: definition
                ? estimateTokenCount(JSON.stringify(definition.raw, null, 2))
                : null,
        };
    })
        .value();
};
const getToolInputDisplay = (toolName, input) => {
    if (toolName !== 'call_tool' || !isRecord(input)) {
        return { toolName, input };
    }
    const innerToolName = input['toolName'];
    if (!_.isString(innerToolName)) {
        return { toolName, input };
    }
    return {
        toolName: innerToolName,
        input: 'input' in input
            ? input['input']
            : 'parameters' in input
                ? input['parameters']
                : {},
    };
};
const parseSkillOutputBlocks = (output) => {
    if (!_.isString(output)) {
        return {};
    }
    return _.chain(Array.from(output.matchAll(/<skill\s+slug="([^"]+)"[^>]*>([\s\S]*?)<\/skill>/g)))
        .map(matchResult => {
        const slug = matchResult[1];
        const content = matchResult[2];
        if (!slug || !content) {
            return null;
        }
        return {
            slug,
            title: getXmlTagText(content, 'title') ?? slug,
            content,
        };
    })
        .compact()
        .keyBy(block => block.slug)
        .value();
};
const getXmlTagText = (content, tag) => {
    const matchResult = content.match(new RegExp(`<${tag}>([\\s\\S]*?)</${tag}>`));
    return matchResult?.[1]?.replace(/\s+/g, ' ').trim() || null;
};
const parseLookupToolDefinitionOutput = (output) => {
    const definitions = (() => {
        if (_.isArray(output)) {
            return output;
        }
        if (!_.isString(output)) {
            return [];
        }
        const jsonStart = output.indexOf('[');
        if (jsonStart < 0) {
            return [];
        }
        try {
            const parsed = JSON.parse(output.slice(jsonStart));
            return _.isArray(parsed) ? parsed : [];
        }
        catch {
            return [];
        }
    })();
    return _.chain(definitions)
        .map(definition => {
        if (!isRecord(definition) ||
            !_.isString(definition['toolName']) ||
            definition['error'] != null) {
            return null;
        }
        return {
            toolName: definition['toolName'],
            raw: definition,
        };
    })
        .compact()
        .value();
};
const getInputStringArray = (input, key) => isRecord(input) && _.isArray(input[key]) ? input[key].filter(_.isString) : [];
const isCompleteToolReadList = (items) => items.length > 0 && _.every(items, item => item.tokenCount != null);
const isRecord = (value) => _.isPlainObject(value);
const getItemID = (item, index) => match(item)
    .with({ type: 'reasoning' }, i => `reasoning-${index}-${i.block.timestamp}`)
    .with({ type: 'progress-note' }, i => `progress-${index}-${i.block.timestamp}`)
    .with({ type: 'response-message' }, i => `response-${index}-${i.block.timestamp}`)
    .with({ type: 'search' }, i => `search-${i.block.tool.id}`)
    .with({ type: 'presentation' }, i => `presentation-${i.block.tool.id}`)
    .with({ type: 'change' }, i => `change-${i.block.tool.id}`)
    .with({ type: 'error' }, i => `error-${index}-${i.block.timestamp}`)
    .with({ type: 'step' }, () => `step-${index}`)
    .exhaustive();
const getTimelineActivitySummaryLabel = (timeline, thinkingTimeText, canExpand) => canExpand
    ? [
        thinkingTimeText,
        formatCountLabel(timeline.totalSkillCount, 'skill', 'skills'),
        formatCountLabel(timeline.totalToolCount, 'tool', 'tools'),
        formatCountLabel(timeline.totalProgressNoteCount, 'progress note', 'progress notes'),
        formatCountLabel(timeline.totalChangeCount, 'change', 'changes'),
    ]
        .filter(label => label != null)
        .join(' • ')
    : thinkingTimeText;
const formatCountLabel = (count, singular, plural) => {
    if (count === 0) {
        return null;
    }
    return `${count} ${count === 1 ? singular : plural}`;
};
const countSkillsRead = (items) => _.chain(items)
    .filter((item) => item.type === 'search' && item.block.tool.name === 'skills_read')
    .flatMap(item => item.block.tool.input['skillSlugs'])
    .filter(_.isString)
    .uniq()
    .size()
    .value();
export const groupTimelineBlocks = (content, isStreaming = false) => {
    const finalTextStartIndex = isStreaming ? -1 : getFinalTextStartIndex(content);
    const firstTs = _.head(content)?.timestamp;
    const lastTs = _.last(content)?.timestamp;
    const errorFallbackText = _.chain(content)
        .filter((block) => block.type === 'error')
        .map(block => block.error)
        .join('\n\n')
        .value();
    const fallbackFinalText = {
        role: 'assistant',
        timestamp: lastTs ?? firstTs ?? new Date(0).toISOString(),
        type: 'text',
        text: errorFallbackText,
    };
    const finalTextBlocks = _.chain(finalTextStartIndex >= 0
        ? content.slice(finalTextStartIndex)
        : isStreaming
            ? []
            : [fallbackFinalText])
        .filter((block) => block?.type === 'text')
        .value();
    const finalResponseBlock = {
        ...(finalTextBlocks[0] ?? fallbackFinalText),
        text: finalTextBlocks.map(block => block.text).join('\n\n'),
    };
    const buildSegment = (items) => ({
        items,
        reasoningCount: _.filter(items, { type: 'reasoning' }).length,
        progressNoteCount: _.filter(items, { type: 'progress-note' }).length,
        skillCount: countSkillsRead(items),
        changeCount: _.filter(items, { type: 'change' }).length,
        toolCount: _.filter(items, i => i.type !== 'reasoning' &&
            i.type !== 'progress-note' &&
            i.type !== 'response-message' &&
            i.type !== 'step' &&
            i.type !== 'change' &&
            !(i.type === 'search' && i.block.tool.name === 'skills_read')).length,
    });
    const activityItems = _.chain(content)
        .map((block, index) => {
        if (block.type === 'text' &&
            finalTextStartIndex >= 0 &&
            index >= finalTextStartIndex) {
            return index === finalTextStartIndex
                ? { type: 'response-message', block: finalResponseBlock }
                : null;
        }
        if (block.type === 'text' &&
            (finalTextStartIndex === -1 || index < finalTextStartIndex)) {
            return block.text.trim().length > 0
                ? { type: 'progress-note', block }
                : null;
        }
        const item = match(block)
            .with({ type: 'reasoning' }, b => {
            const summary = b.reasoning.summary?.trim() ?? '';
            if (summary === '' || summary === '[REDACTED]') {
                return null;
            }
            return {
                type: 'reasoning',
                block: b,
            };
        })
            .with({ type: 'search' }, b => ({
            type: 'search',
            block: b,
        }))
            .with({ type: 'presentation' }, b => ({
            type: 'presentation',
            block: b,
        }))
            .with({ type: 'change' }, b => ({
            type: 'change',
            block: b,
        }))
            .with({ type: 'error' }, b => ({
            type: 'error',
            block: b,
        }))
            .with({ type: 'new-step' }, b => ({
            type: 'step',
            block: b,
        }))
            .otherwise(() => null);
        return item;
    })
        .compact()
        .thru(items => finalTextStartIndex >= 0 || isStreaming
        ? items
        : [
            ...items,
            {
                type: 'response-message',
                block: finalResponseBlock,
            },
        ])
        .thru(removeEmptyStepItems)
        .value();
    const leadingActivity = activityItems.length > 0 ? buildSegment(activityItems) : null;
    const textSegments = _.chain(finalTextBlocks)
        .filter((block) => block?.type === 'text')
        .map(text => ({
        text,
        followingActivity: null,
        isFinal: true,
    }))
        .value();
    const totalReasoningCount = _.filter(activityItems, { type: 'reasoning' }).length;
    const totalProgressNoteCount = _.filter(activityItems, {
        type: 'progress-note',
    }).length;
    const totalSkillCount = countSkillsRead(activityItems);
    const totalChangeCount = _.filter(activityItems, { type: 'change' }).length;
    const totalToolCount = _.filter(activityItems, i => i.type !== 'reasoning' &&
        i.type !== 'progress-note' &&
        i.type !== 'response-message' &&
        i.type !== 'step' &&
        i.type !== 'error' &&
        i.type !== 'change' &&
        !(i.type === 'search' && i.block.tool.name === 'skills_read')).length;
    let totalThinkingSeconds = 0;
    if (firstTs && lastTs) {
        try {
            const first = new Date(firstTs).getTime();
            const last = new Date(lastTs).getTime();
            totalThinkingSeconds = Math.max(0, (last - first) / 1000);
        }
        catch {
        }
    }
    return {
        leadingActivity,
        textSegments,
        firstContentTimestamp: firstTs,
        totalThinkingSeconds,
        totalReasoningCount,
        totalProgressNoteCount,
        totalSkillCount,
        totalChangeCount,
        totalToolCount,
    };
};
const getFinalTextStartIndex = (content) => {
    const lastVisibleActivityIndex = _.findLastIndex(content, isVisibleActivityBlock);
    const firstFinalTextOffset = _.findIndex(content.slice(lastVisibleActivityIndex + 1), block => block.type === 'text');
    return firstFinalTextOffset < 0
        ? -1
        : lastVisibleActivityIndex + 1 + firstFinalTextOffset;
};
const isVisibleActivityBlock = (block) => {
    if (block.type === 'reasoning') {
        const summary = block.reasoning.summary?.trim() ?? '';
        return summary !== '' && summary !== '[REDACTED]';
    }
    return (block.type === 'search' ||
        block.type === 'presentation' ||
        block.type === 'change' ||
        block.type === 'error');
};
const removeEmptyStepItems = (items) => _.chain(items)
    .map((item, index) => ({
    item,
    hasVisibleActivityBeforeNextStep: _.some(_.takeWhile(items.slice(index + 1), next => next.type !== 'step'), next => next.type !== 'step'),
}))
    .filter(entry => entry.item.type !== 'step' || entry.hasVisibleActivityBeforeNextStep)
    .map(entry => entry.item)
    .value();
//# sourceMappingURL=assistant-timeline.js.map